import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  FileAudio, 
  Trash2, 
  Link, 
  Unlink, 
  ExternalLink,
  Search,
  HardDrive,
  Loader2,
  Play,
  Pause,
  RefreshCw,
  Copy,
  Check,
  Link2
} from "lucide-react";
import { toast } from "sonner";

export default function AdminFileManager() {
  const { token } = useAdminAuth();
  const [files, setFiles] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, linked: 0, unlinked: 0 });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [deleteFile, setDeleteFile] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [playingFile, setPlayingFile] = useState(null);
  const [audioRef, setAudioRef] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  
  // Link dialog state
  const [linkingFile, setLinkingFile] = useState(null);
  const [selectedContentId, setSelectedContentId] = useState("");
  const [contentSearch, setContentSearch] = useState("");
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef) {
        audioRef.pause();
      }
    };
  }, [audioRef]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [filesRes, contentRes] = await Promise.all([
        axios.get(`${API}/admin/files`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/content`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFiles(filesRes.data.files || []);
      setContent(contentRes.data || []);
      setStats({
        total: filesRes.data.total,
        linked: filesRes.data.linked,
        unlinked: filesRes.data.unlinked
      });
    } catch (error) {
      console.error("Failed to fetch data", error);
      toast.error("Failed to load files");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteFile) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/files/${deleteFile.filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("File deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete file");
    } finally {
      setDeleting(false);
      setDeleteFile(null);
    }
  };

  const handleLink = async () => {
    if (!linkingFile || !selectedContentId) {
      console.log('Missing data:', { linkingFile, selectedContentId });
      toast.error('Please select a content item to link');
      return;
    }
    
    console.log('Linking file:', linkingFile.file_url, 'to content:', selectedContentId);
    
    setLinking(true);
    try {
      const response = await axios.put(
        `${API}/admin/content/${selectedContentId}`,
        { file_url: linkingFile.file_url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Link response:', response.data);
      toast.success(`File linked to "${response.data.title}" successfully!`);
      setLinkingFile(null);
      setSelectedContentId("");
      setContentSearch("");
      fetchData();
    } catch (error) {
      console.error('Link error:', error);
      toast.error(error.response?.data?.detail || "Failed to link file");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (file) => {
    if (!file.linked_content) return;
    
    try {
      // Set the file_url to empty or a placeholder
      await axios.put(
        `${API}/admin/content/${file.linked_content.content_id}`,
        { file_url: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("File unlinked successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to unlink file");
    }
  };

  const openLinkDialog = (file) => {
    setLinkingFile(file);
    // Pre-select current linked content if any
    if (file.linked_content) {
      setSelectedContentId(file.linked_content.content_id);
    } else {
      setSelectedContentId("");
    }
    setContentSearch("");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getFullUrl = (fileUrl) => {
    // For admin file manager, use the direct file URL which requires admin auth
    const baseUrl = API.replace('/api', '');
    // Extract just the filename from the URL
    const filename = fileUrl.split('/').pop();
    return `${API}/admin/stream-file/${filename}`;
  };

  const copyToClipboard = async (fileUrl) => {
    const filename = fileUrl.split('/').pop();
    const fullUrl = `${window.location.origin}${API}/admin/stream-file/${filename}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(fileUrl);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      toast.error("Failed to copy URL");
    }
  };

  const togglePlay = async (file) => {
    // Stop any currently playing audio
    if (audioRef) {
      audioRef.pause();
    }
    
    if (playingFile === file.filename) {
      setPlayingFile(null);
      return;
    }
    
    try {
      // Get a signed URL for admin playback
      const response = await axios.get(`${API}/admin/stream-file/${file.filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audio.play();
      audio.onended = () => {
        setPlayingFile(null);
        URL.revokeObjectURL(audioUrl);
      };
      setAudioRef(audio);
      setPlayingFile(file.filename);
    } catch (error) {
      console.error("Failed to play file:", error);
      toast.error("Failed to play audio file");
    }
  };

  // Filter files
  const filteredFiles = files.filter(file => {
    const matchesSearch = file.filename.toLowerCase().includes(search.toLowerCase()) ||
      (file.linked_content?.title || "").toLowerCase().includes(search.toLowerCase());
    
    const matchesFilter = filter === "all" ||
      (filter === "linked" && file.linked_content) ||
      (filter === "unlinked" && !file.linked_content);
    
    return matchesSearch && matchesFilter;
  });

  // Filter content for link dialog
  const filteredContent = content.filter(item => {
    if (!contentSearch) return true;
    return item.title?.toLowerCase().includes(contentSearch.toLowerCase()) ||
      item.course_name?.toLowerCase().includes(contentSearch.toLowerCase());
  });

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-bold">File Manager</h1>
          <p className="text-muted-foreground">Manage uploaded audio files and link them to content</p>
        </div>
        <Button onClick={fetchData} variant="outline" disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <FileAudio className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Files</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Link className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.linked}</p>
              <p className="text-xs text-muted-foreground">Linked to Content</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Unlink className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.unlinked}</p>
              <p className="text-xs text-muted-foreground">Unlinked Files</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <HardDrive className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatFileSize(totalSize)}</p>
              <p className="text-xs text-muted-foreground">Total Size</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search files or linked content..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("all")}
              >
                All ({stats.total})
              </Button>
              <Button
                variant={filter === "linked" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("linked")}
                className={filter === "linked" ? "bg-green-600 hover:bg-green-700" : ""}
              >
                <Link className="mr-1 h-3 w-3" />
                Linked ({stats.linked})
              </Button>
              <Button
                variant={filter === "unlinked" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("unlinked")}
                className={filter === "unlinked" ? "bg-amber-600 hover:bg-amber-700" : ""}
              >
                <Unlink className="mr-1 h-3 w-3" />
                Unlinked ({stats.unlinked})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* File List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gold" />
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-12">
              <FileAudio className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {search || filter !== "all" ? "No files match your filters" : "No files uploaded yet"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead>Linked Content</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFiles.map((file) => (
                  <TableRow key={file.filename}>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => togglePlay(file)}
                      >
                        {playingFile === file.filename ? (
                          <Pause className="h-4 w-4 text-gold" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileAudio className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-mono text-sm truncate max-w-[200px]">
                          {file.filename}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {file.linked_content ? (
                        <div>
                          <p className="font-medium">{file.linked_content.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {file.linked_content.course_name} • {file.linked_content.vibe}
                          </p>
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-600">
                          <Unlink className="mr-1 h-3 w-3" />
                          Not linked
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatFileSize(file.size)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(file.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Link/Re-link Button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600"
                          onClick={() => openLinkDialog(file)}
                          title={file.linked_content ? "Change linked content" : "Link to content"}
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                        {/* Unlink Button (only if linked) */}
                        {file.linked_content && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-amber-500 hover:text-amber-600"
                            onClick={() => handleUnlink(file)}
                            title="Unlink from content"
                          >
                            <Unlink className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => copyToClipboard(file.file_url)}
                          title="Copy URL"
                        >
                          {copiedUrl === file.file_url ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => window.open(getFullUrl(file.file_url), "_blank")}
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => setDeleteFile(file)}
                          disabled={!!file.linked_content}
                          title={file.linked_content ? "Unlink first to delete" : "Delete file"}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Link Dialog */}
      <Dialog open={!!linkingFile} onOpenChange={(open) => !open && setLinkingFile(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-gold" />
              Link File to Content
            </DialogTitle>
          </DialogHeader>
          
          {linkingFile && (
            <div className="space-y-4">
              {/* Current File */}
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground">Selected File</Label>
                <p className="font-mono text-sm mt-1">{linkingFile.filename}</p>
                {linkingFile.linked_content && (
                  <p className="text-xs text-amber-500 mt-1">
                    Currently linked to: {linkingFile.linked_content.title}
                  </p>
                )}
              </div>

              {/* Search Content */}
              <div className="space-y-2">
                <Label>Search Content</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by title or course..."
                    value={contentSearch}
                    onChange={(e) => setContentSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Content List */}
              <div className="space-y-2">
                <Label>Select Content to Link</Label>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  {filteredContent.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No content found
                    </p>
                  ) : (
                    filteredContent.map(item => (
                      <button
                        key={item.content_id}
                        className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-muted transition-colors ${
                          selectedContentId === item.content_id ? "bg-gold/20 border-gold" : ""
                        }`}
                        onClick={() => setSelectedContentId(item.content_id)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{item.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.course_name} • <span className="font-semibold uppercase">{item.vibe}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            {item.file_url?.includes("/api/uploads/") && (
                              <span className="text-xs text-green-500 bg-green-500/10 px-2 py-0.5 rounded">Your Audio</span>
                            )}
                            {item.file_url?.includes("soundhelix") && (
                              <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded">Demo</span>
                            )}
                            {!item.file_url && (
                              <span className="text-xs text-red-500 bg-red-500/10 px-2 py-0.5 rounded">No Audio</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setLinkingFile(null)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleLink} 
                  disabled={!selectedContentId || linking}
                  className="bg-gold text-primary-foreground"
                >
                  {linking ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="mr-2 h-4 w-4" />
                  )}
                  Link File
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteFile} onOpenChange={() => setDeleteFile(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteFile?.filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
